import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useAuth, API } from '../context/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { MapPin, DollarSign, Loader2 } from 'lucide-react';
import MapView from './MapView';
import GoogleTaxiMap from './GoogleTaxiMap';

const DEFAULT_PICKUP = { lat: -3.4488, lng: -79.9595, address: 'Santa Rosa, El Oro', source: 'default' };
const DEFAULT_DROPOFF = { lat: -3.4524, lng: -79.9632, address: 'Destino en Santa Rosa', source: 'manual' };
const TAXI_SETTINGS = {
  base_fare: 1,
  price_per_km: 0.5,
  price_per_minute: 0.08,
  minimum_fare: 1.5
};

const RideRequestModal = ({ open, onOpenChange, onRideCreated }) => {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [pickup, setPickup] = useState(null);
  const [dropoff, setDropoff] = useState(null);
  const [pickupReference, setPickupReference] = useState('');
  const [currentPrice, setCurrentPrice] = useState(2);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashBill, setCashBill] = useState('exact');
  const [creating, setCreating] = useState(false);
  const [geoMessage, setGeoMessage] = useState('');
  const [createError, setCreateError] = useState('');
  const [googleRouteInfo, setGoogleRouteInfo] = useState(null);
  const [googleMapsUnavailable, setGoogleMapsUnavailable] = useState(false);
  const [taxiSettings, setTaxiSettings] = useState({});
  const [mapTarget, setMapTarget] = useState('pickup');
  const googleMapsEnabled = Boolean(process.env.REACT_APP_GOOGLE_MAPS_API_KEY) && !googleMapsUnavailable;
  const taxiPricing = useMemo(() => ({
    base_fare: Number(taxiSettings.base_fare ?? TAXI_SETTINGS.base_fare),
    price_per_km: Number(taxiSettings.price_per_km ?? TAXI_SETTINGS.price_per_km),
    price_per_minute: Number(taxiSettings.price_per_minute ?? TAXI_SETTINGS.price_per_minute),
    minimum_fare: Math.max(Number(taxiSettings.minimum_fare ?? TAXI_SETTINGS.minimum_fare) || TAXI_SETTINGS.minimum_fare, 0.5)
  }), [taxiSettings]);
  const minimumOffer = Math.max(Number(taxiPricing.minimum_fare || 0), 0.5);

  useEffect(() => {
    if (open) {
      setStep(1);
      setPickup(null);
      setDropoff(null);
      setPickupReference('');
      setCurrentPrice(2);
      setPaymentMethod('cash');
      setCashBill('exact');
      setGeoMessage('');
      setCreateError('');
      setGoogleRouteInfo(null);
      setGoogleMapsUnavailable(false);
      setMapTarget('pickup');
      axios.get(`${API}/taxi/settings`, { withCredentials: true })
        .then(({ data }) => setTaxiSettings(data || {}))
        .catch(() => setTaxiSettings({}));
    }
  }, [open]);

  const calculateDistance = (a, b) => {
    if (!a || !b || !a.lat || !b.lat) return 0;
    const R = 6371;
    const dLat = (b.lat - a.lat) * Math.PI / 180;
    const dLng = (b.lng - a.lng) * Math.PI / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(a.lat * Math.PI / 180) * Math.cos(b.lat * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
  };

  const fallbackRouteInfo = useMemo(() => {
    const distanceKm = calculateDistance(pickup, dropoff);
    const durationMin = Math.max(Math.round(distanceKm * 3), 3);
    const rawPrice = taxiPricing.base_fare + distanceKm * taxiPricing.price_per_km + durationMin * taxiPricing.price_per_minute;
    const suggested = Math.round(Math.max(rawPrice, minimumOffer) * 4) / 4;
    return { distance_km: distanceKm, duration_minutes: durationMin, suggested_price: suggested, provider: 'fallback' };
  }, [pickup, dropoff, taxiPricing, minimumOffer]);

  const routeInfo = useMemo(() => {
    const base = googleRouteInfo?.distance_km && googleRouteInfo?.duration_minutes ? googleRouteInfo : fallbackRouteInfo;
    const rawPrice = taxiPricing.base_fare + Number(base.distance_km || 0) * taxiPricing.price_per_km + Number(base.duration_minutes || 0) * taxiPricing.price_per_minute;
    const suggested = Math.round(Math.max(rawPrice, minimumOffer) * 4) / 4;
    return { ...base, suggested_price: suggested };
  }, [googleRouteInfo, fallbackRouteInfo, taxiPricing, minimumOffer]);

  useEffect(() => {
    if (pickup && dropoff) {
      setCurrentPrice(Math.max(routeInfo.suggested_price, minimumOffer));
    }
  }, [pickup, dropoff, routeInfo.suggested_price, minimumOffer]);

  const useCurrentLocation = () => {
    setMapTarget('pickup');
    if (!navigator.geolocation) {
      setGeoMessage('Tu navegador puede requerir HTTPS para usar ubicacion exacta. Puedes seleccionar la ubicacion manualmente.');
      setPickup(DEFAULT_PICKUP);
      setMapTarget('dropoff');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setPickup({
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          address: 'Mi ubicacion actual',
          source: 'gps',
          accuracy: position.coords.accuracy
        });
        setMapTarget('dropoff');
        setGeoMessage('Ahora selecciona tu destino.');
      },
      () => {
        setGeoMessage('Tu navegador puede requerir HTTPS para usar ubicacion exacta. Puedes seleccionar la ubicacion manualmente.');
        setPickup(DEFAULT_PICKUP);
        setMapTarget('dropoff');
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 }
    );
  };

  const updatePickup = useCallback((point) => {
    setCreateError('');
    setPickup((current) => ({ ...(current || {}), ...point }));
  }, []);

  const updateDropoff = useCallback((point) => {
    setCreateError('');
    setDropoff((current) => ({ ...(current || {}), ...point }));
  }, []);

  const selectFallbackMapPoint = (point) => {
    if (mapTarget === 'pickup') {
      setPickup({ ...point, address: `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`, source: 'map' });
    } else {
      setDropoff({ ...point, address: `${point.lat.toFixed(4)}, ${point.lng.toFixed(4)}`, source: 'map' });
    }
  };

  const updateGoogleRoute = useCallback((info) => {
    setGoogleRouteInfo(info);
  }, []);

  const disableGoogleMaps = useCallback(() => {
    setGoogleMapsUnavailable(true);
  }, []);

  const setOfferPrice = (value) => {
    const next = Math.max(Number(value) || minimumOffer, minimumOffer);
    setCurrentPrice(Number(next.toFixed(2)));
  };

  const adjustOfferPrice = (delta) => {
    const base = Number.isFinite(Number(currentPrice)) ? Number(currentPrice) : routeInfo.suggested_price;
    setOfferPrice(Math.round((base + delta) * 100) / 100);
  };

  const resetSuggestedPrice = () => {
    setOfferPrice(routeInfo.suggested_price);
  };

  const toggleCashBill = (value) => {
    setCashBill((current) => (current === value && value !== 'exact' ? 'exact' : value));
  };

  const goNextFromLocation = () => {
    if (!pickup?.lat || !pickup?.lng) {
      setCreateError('Selecciona donde te recogemos.');
      setMapTarget('pickup');
      return;
    }
    if (!dropoff?.lat || !dropoff?.lng) {
      setCreateError('Selecciona a donde vas.');
      setMapTarget('dropoff');
      return;
    }
    setCreateError('');
    setStep(2);
  };

  const handleCreateRide = async () => {
    if (!pickup?.lat || !pickup?.lng) {
      setCreateError('Selecciona donde te recogemos.');
      setStep(1);
      setMapTarget('pickup');
      return;
    }
    if (!dropoff?.lat || !dropoff?.lng) {
      setCreateError('Selecciona a donde vas.');
      setStep(1);
      setMapTarget('dropoff');
      return;
    }
    const offeredPrice = Number(currentPrice);
    if (!Number.isFinite(offeredPrice) || offeredPrice < minimumOffer) {
      setCreateError(`La oferta minima es $${minimumOffer.toFixed(2)}.`);
      return;
    }
    setCreating(true);
    setCreateError('');
    try {
      const response = await axios.post(`${API}/rides/request`, {
        client_id: user._id,
        pickup_location: {
          lat: pickup.lat,
          lng: pickup.lng,
          address: routeInfo.pickup_address || pickup.address || 'Origen',
          source: pickup.source || 'manual',
          reference: pickupReference,
          place_id: pickup.place_id,
          accuracy: pickup.accuracy
        },
        dropoff_location: {
          lat: dropoff.lat,
          lng: dropoff.lng,
          address: routeInfo.dropoff_address || dropoff.address || 'Destino',
          source: dropoff.source || 'manual',
          place_id: dropoff.place_id
        },
        pickup_reference: pickupReference,
        suggested_price: routeInfo.suggested_price,
        offered_price: offeredPrice,
        estimated_distance: routeInfo.distance_km,
        estimated_duration: routeInfo.duration_minutes,
        payment_method: paymentMethod,
        cash_bill: paymentMethod === 'cash' ? cashBill : null,
        transfer_status: paymentMethod === 'transfer' ? 'pending' : null,
        route_info: routeInfo
      }, { withCredentials: true });

      if (onRideCreated) onRideCreated(response.data);
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating ride:', error);
      const detail = error.response?.data?.detail || (error.request ? 'No se pudo conectar con el servidor' : 'Error al crear viaje');
      setCreateError(detail);
      alert(detail);
    } finally {
      setCreating(false);
    }
  };

  const setManualPickup = (address) => {
    setPickup((p) => ({ ...(p || DEFAULT_PICKUP), address, source: 'manual' }));
  };

  const setManualDropoff = (address) => {
    setDropoff((p) => ({ ...(p || DEFAULT_DROPOFF), address, source: 'manual' }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="ride-request-modal" className="bg-[#0B0C10] border-white/10 text-white w-[calc(100vw-1rem)] sm:max-w-3xl max-h-[95vh] sm:max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-medium text-white" style={{ fontFamily: 'Outfit' }}>
            Solicitar taxi
          </DialogTitle>
          {googleMapsEnabled ? (
            <p className="text-xs text-[#00F0FF]">Google Maps activo: busqueda, ruta y tiempo real habilitados.</p>
          ) : (
            <p className="text-xs text-yellow-300">Mapa alternativo activo. Puedes pedir taxi sin Google Maps.</p>
          )}
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <div className="grid grid-cols-3 gap-2 text-[11px] sm:text-xs text-center">
              {['Ruta', 'Precio y pago', 'Confirmacion'].map((label, index) => {
                const value = index + 1;
                const activeStep = creating ? 3 : step;
                return (
                  <div key={label} className={activeStep >= value ? 'text-white' : 'text-[#6B7280]'}>
                    {label}
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2">
              {[1, 2, 3].map((s) => (
                <div key={s} className={`flex-1 h-1 rounded-full ${(creating ? 3 : step) >= s ? 'bg-gradient-to-r from-[#FF007F] to-[#8A2BE2]' : 'bg-white/10'}`} />
              ))}
            </div>
          </div>

          {step === 1 && (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Label className="text-white flex items-center gap-2"><MapPin className="w-4 h-4 text-[#00F0FF]" />Ruta del viaje</Label>
              </div>
              {!googleMapsEnabled && (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <Button type="button" onClick={() => setMapTarget('pickup')} className={`${mapTarget === 'pickup' ? 'bg-[#00F0FF]/20 border-[#00F0FF]/50 text-[#BFFBFF]' : 'bg-white/5 border-white/10 text-white'} border rounded-xl`}>
                      Donde estoy
                    </Button>
                    <Button type="button" onClick={() => setMapTarget('dropoff')} className={`${mapTarget === 'dropoff' ? 'bg-[#FF007F]/20 border-[#FF007F]/50 text-[#FFD1E8]' : 'bg-white/5 border-white/10 text-white'} border rounded-xl`}>
                      A donde voy
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#D1D5DB]">Donde estas</Label>
                      <Input data-testid="ride-pickup-address" placeholder="Donde te recogemos" value={pickup?.address || ''} onFocus={() => setMapTarget('pickup')} onChange={(e) => setManualPickup(e.target.value)} className="bg-white/5 border-white/10 text-white rounded-xl" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs text-[#D1D5DB]">A donde vas</Label>
                      <Input data-testid="ride-dropoff-address" placeholder="Destino" value={dropoff?.address || ''} onFocus={() => setMapTarget('dropoff')} onChange={(e) => setManualDropoff(e.target.value)} className="bg-white/5 border-white/10 text-white rounded-xl" />
                    </div>
                  </div>
                  <Button type="button" onClick={useCurrentLocation} className="bg-white/5 border border-white/10 text-white rounded-xl">
                    Usar mi ubicacion actual
                  </Button>
                </div>
              )}
              {geoMessage && <p className="text-xs text-yellow-300 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-3">{geoMessage}</p>}
              {googleMapsEnabled ? (
                <GoogleTaxiMap
                  pickup={pickup}
                  dropoff={dropoff}
                  onPickupChange={updatePickup}
                  onDropoffChange={updateDropoff}
                  onRouteChange={updateGoogleRoute}
                  onFallback={disableGoogleMaps}
                  markerIcons={taxiSettings}
                  activeTarget={mapTarget}
                  onActiveTargetChange={setMapTarget}
                  height="340px"
                />
              ) : (
                <MapView
                  pickup={pickup}
                  dropoff={dropoff}
                  clickable
                  draggableMarkers
                  onLocationSelect={selectFallbackMapPoint}
                  onPickupDrag={(pt) => setPickup({ ...pt, address: `${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}`, source: 'map' })}
                  onDropoffDrag={(pt) => setDropoff({ ...pt, address: `${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}`, source: 'map' })}
                  height="340px"
                />
              )}
              <Input placeholder="Referencia adicional: casa azul, frente a farmacia..." value={pickupReference} onChange={(e) => setPickupReference(e.target.value)} className="bg-white/5 border-white/10 text-white rounded-xl" />
              <Button data-testid="ride-step-next" onClick={goNextFromLocation} className="w-full gradient-button rounded-xl py-5">
                Continuar al precio
              </Button>
              {createError && step === 1 && (
                <p className="text-sm text-red-200 bg-red-500/10 border border-red-500/25 rounded-xl p-3">{createError}</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <p className="text-xs text-[#9CA3AF]">Recogida</p>
                  <p className="text-sm text-white">{pickup?.address}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <p className="text-xs text-[#9CA3AF]">Destino</p>
                  <p className="text-sm text-white">{dropoff?.address}</p>
                </div>
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
                  <p className="text-xs text-[#9CA3AF]">Ruta estimada</p>
                  <p className="text-sm text-white">{routeInfo.distance_km.toFixed(1)} km · {routeInfo.duration_minutes} min</p>
                </div>
              </div>

              {googleMapsEnabled ? (
                <GoogleTaxiMap
                  pickup={pickup}
                  dropoff={dropoff}
                  onPickupChange={updatePickup}
                  onDropoffChange={updateDropoff}
                  onRouteChange={updateGoogleRoute}
                  onFallback={disableGoogleMaps}
                  markerIcons={taxiSettings}
                  activeTarget="dropoff"
                  height="260px"
                  showInputs={false}
                  showLocationButton={false}
                  readOnly
                />
              ) : (
                <MapView pickup={pickup} dropoff={dropoff} height="210px" route={routeInfo.route_path?.map((p) => [p.lat, p.lng])} />
              )}

              <div className="glass-card p-5 rounded-2xl">
                <p className="text-xs text-[#9CA3AF] uppercase tracking-widest mb-1">Precio sugerido</p>
                <p className="text-3xl text-white font-semibold flex items-center gap-1" style={{ fontFamily: 'Outfit' }}>
                  <DollarSign className="w-7 h-7 text-[#FF007F]" />{routeInfo.suggested_price.toFixed(2)}
                </p>
                <Label className="text-[#D1D5DB] mt-5 mb-3 block">Tu oferta</Label>
                <div className="grid grid-cols-[minmax(88px,1fr)_auto_minmax(88px,1fr)] items-center gap-2 sm:gap-3">
                  <Button
                    type="button"
                    onClick={() => adjustOfferPrice(-0.25)}
                    disabled={Number(currentPrice || 0) <= minimumOffer}
                    className="bg-emerald-900/70 hover:bg-emerald-800/80 disabled:opacity-40 text-white rounded-2xl min-h-[52px] px-2"
                  >
                    -$0.25
                  </Button>
                  <div className="min-w-[128px] text-center bg-black/25 border border-white/10 rounded-2xl px-4 py-3">
                    <p className="text-xs text-[#9CA3AF]">Oferta actual</p>
                    <p data-testid="ride-current-price" className="text-3xl sm:text-4xl font-semibold text-white" style={{ fontFamily: 'Outfit' }}>
                      ${Number(currentPrice || 0).toFixed(2)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={() => adjustOfferPrice(0.25)}
                    className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold rounded-2xl min-h-[52px] px-2"
                  >
                    +$0.25
                  </Button>
                </div>
                <Button
                  type="button"
                  onClick={resetSuggestedPrice}
                  className="mt-3 bg-white/5 border border-white/10 text-white hover:bg-white/10 rounded-xl"
                >
                  Restablecer precio sugerido
                </Button>
                {Number(currentPrice || 0) < routeInfo.suggested_price && (
                  <p className="text-xs text-yellow-300 mt-2">Una oferta menor puede tardar mas en ser aceptada.</p>
                )}
                {Number(currentPrice || 0) > routeInfo.suggested_price && (
                  <p className="text-xs text-emerald-300 mt-2">Mejor oferta para respuesta rapida.</p>
                )}
              </div>

              <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-3">
                <Label className="text-white">Forma de pago</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button onClick={() => setPaymentMethod('cash')} className={`${paymentMethod === 'cash' ? 'gradient-button' : 'bg-white/5 border border-white/10 text-white'} rounded-xl`}>Efectivo</Button>
                  <Button onClick={() => setPaymentMethod('transfer')} className={`${paymentMethod === 'transfer' ? 'gradient-button' : 'bg-white/5 border border-white/10 text-white'} rounded-xl`}>Transferencia</Button>
                </div>
                {paymentMethod === 'cash' ? (
                  <>
                    <p className="text-xs text-[#9CA3AF]">Indica con que billete pagaras para que el conductor lleve cambio.</p>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      {[
                        ['exact', 'Pago exacto'],
                        ['5', 'Billete de $5'],
                        ['10', 'Billete de $10'],
                        ['20', 'Billete de $20']
                      ].map(([value, label]) => (
                        <Button key={value} onClick={() => toggleCashBill(value)} className={`${cashBill === value ? 'gradient-button' : 'bg-white/5 border border-white/10 text-white'} rounded-xl text-xs`}>
                          {label}
                        </Button>
                      ))}
                    </div>
                    <p className="text-xs text-[#D1D5DB]">
                      {cashBill === 'exact'
                        ? 'Pagaras en efectivo con pago exacto.'
                        : `Pagaras en efectivo con billete de $${cashBill}. El conductor debe llevar cambio.`}
                    </p>
                  </>
                ) : (
                  <p className="text-xs text-[#9CA3AF]">Pagaras por transferencia al conductor. El conductor confirmara el pago recibido.</p>
                )}
              </div>

              <div className="sticky bottom-0 z-10 -mx-6 -mb-6 px-6 py-4 bg-[#0B0C10]/95 backdrop-blur border-t border-white/10 flex gap-2">
                <Button onClick={() => setStep(1)} className="bg-white/5 border border-white/10 rounded-xl text-white">Atrás</Button>
                <Button data-testid="ride-submit-button" onClick={handleCreateRide} disabled={creating || !pickup?.lat || !dropoff?.lat || !Number(currentPrice)} className="flex-1 gradient-button rounded-xl py-5">
                  {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : `Solicitar taxi por $${Number(currentPrice || 0).toFixed(2)}`}
                </Button>
              </div>
              {createError && (
                <p className="text-sm text-red-200 bg-red-500/10 border border-red-500/25 rounded-xl p-3">{createError}</p>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RideRequestModal;
